import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery} from "react-query";
import axios, {AxiosResponse} from "axios";
import {
    Button,
    Descriptions,
    Divider,
    Flex,
    Form,
    Image,
    Input,
    List,
    message,
    Modal,
    Popconfirm,
    Typography
} from "antd";
import {EventDTO, ExistsDto} from "./types";
import AddButton from "../commons/AddButton";
import moment from "moment";
import {useForm} from "antd/lib/form/Form";
import TextArea from "antd/es/input/TextArea";
import {defaultFormItemLayout} from "../commons/forms";
import dayjs from "dayjs";
import {Link} from "react-router-dom";
import DescriptionsItem from "antd/es/descriptions/Item";
import {MobileDatePicker} from "../commons/MobileDatePicker";
import {CheckOutlined, CloseOutlined, CopyOutlined} from "@ant-design/icons";
import {useClipboard} from "../commons/utils";
import {useTranslation} from "react-i18next";
import {Label} from "../commons/translations/Label";

export const EventsEditor: React.FC = () => {
    const {t} = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [form] = useForm<EventDTO | undefined>(undefined);
    const [editableEvent, setEditableEvent] = useState<EventDTO | undefined>(undefined);
    const {isLoading, data, refetch} = useQuery('lodge-admin-events',
        () => axios.get<object, AxiosResponse<EventDTO[]>>(`/api/admin/events`).then(r => r.data)
    )
    const {copyToClipboard} = useClipboard();

    const events = useMemo<EventDTO[]>(() => data ? data : [], [data]);

    useEffect(() => {
        if (!!editableEvent) {
            form.setFieldsValue({...editableEvent, startsAt: dayjs(editableEvent.startsAt)});
        }
    }, [editableEvent, form]);

    const submitChanges = useCallback((data: EventDTO) => {
        const toSend = {...data, ...{
            startsAt: data.startsAt.valueOf()
        }}
        const request = (!editableEvent)
            ? () => axios.post(`/api/admin/events`, toSend)
            : () => axios.put(`/api/admin/events/${editableEvent?.id}`, toSend);
        request()
            .then(() => {
                setEditableEvent(undefined);
                form.resetFields();
                setIsModalOpen(false);
                message.success(editableEvent ? t('eventEditor.success.update') : t('eventEditor.success.create'));
                refetch();
            })
            .catch(() => {
               message.error(editableEvent ? t('eventEditor.errors.update') : t('eventEditor.errors.create'));
               setEditableEvent(undefined);
            });
    }, [form, editableEvent, setEditableEvent, refetch, t]);

    const editEvent = useCallback((event: EventDTO) => {
        setEditableEvent(event);
        setIsModalOpen(true);
    }, [setEditableEvent, setIsModalOpen]);

    const cancelEvent = useCallback((event: EventDTO) => {
        axios.delete(`/api/admin/events/${event.id}`)
        .then(() => {
            message.success('Event canceled!');
            refetch();
        })
        .catch(() => {
            message.error('Could not cancel. Please try again or refresh.')
        })
    }, [refetch]);

    const publishEvent = useCallback((event: EventDTO) => {
        axios.put(`/api/admin/events/${event.id}/access`, undefined)
        .then(() => {
            message.success('Event published!');
            refetch();
        })
        .catch(() => {
            message.error('Could not publish. Please try again or refresh.')
        })
    }, [refetch]);

    const createNewEvent = useCallback(() => {
        setEditableEvent(undefined);
        setIsModalOpen(true);
    }, [setEditableEvent, setIsModalOpen]);

    const downloadAttendanceSheet = useCallback((event: EventDTO) => {
        axios.get(`/api/admin/reservations/${event.id}/attendance-sheet`, {
            responseType: "blob"
        })
            .then(r => {
                const fileName = r.headers['x-reservation-file-id'] || `${event.title}-attendance-sheet.pdf`;
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(r.data);
                link.download=fileName;
                link.click();
            })
            .catch(e => {
                const messsage = e?.response?.status === 400
                    ? e?.message || t('common.failure')
                    : t('common.failure');
                Modal.error({
                    title: 'Attendance sheet cannot be downloaded.',
                    content: messsage
                });
            })
    }, [t]);

    const validateTag = useCallback((tag: string, existingId: string | undefined) => {
        if (!tag) {
            return false;
        }
        return axios.get<object, AxiosResponse<ExistsDto>>(`/api/admin/events/exists/${tag}`)
        .then((r) => {
          const response = r.data;
          return !response.exists ||
              response.exists && !!existingId && existingId === response.entityId;
        })
    }, [])

    return (
      <>
          <Modal
              open={isModalOpen}
              closable={false}
              onCancel={() => {
                  form.resetFields();
                  setIsModalOpen(false);
              }}
              onOk={form.submit}>
              <Typography.Title level={3}>{editableEvent ? t('eventEditor.title.update') : t('eventEditor.title.create')}</Typography.Title>
              <p>
                  <Typography.Text>Please enter the details below:</Typography.Text>
              </p>
              <Form form={form} {...defaultFormItemLayout} onFinish={submitChanges}>
                  <Form.Item
                      name="title"
                      label={t('eventEditor.form.title')}
                      required={true}
                  >
                      <Input />
                  </Form.Item>
                  <Form.Item
                      name="tag"
                      label={t('eventEditor.form.tag')}
                      required={true}
                      hasFeedback={true}
                      validateDebounce={1000}
                      rules={[
                          {
                              validator: async (rule, value) => {
                                  const isValid = await validateTag(value, editableEvent?.id);
                                  if (isValid) {
                                      return Promise.resolve();
                                  } else {
                                      return Promise.reject(new Error('Tag already exists.'));
                                  }

                              }
                          },
                          {
                              pattern: new RegExp(/^[a-zA-Z0-9-]+$/),
                              message: 'invalid tag'
                          },
                          {min:8},
                          {max:12},
                          {required: true}
                      ]}
                  >
                      <Input placeholder={"Use only letters, numbers and the '-' symbol."} max={12} min={5} />
                  </Form.Item>
                  <Form.Item
                      name="startsAt"
                      label={t('eventEditor.form.startsAt')}
                      required={true}
                  >
                      <MobileDatePicker />
                  </Form.Item>
                  <Form.Item
                      name="location"
                      label={t('eventEditor.form.location')}
                      required={true}
                  >
                      <Input />
                  </Form.Item>
                  <Form.Item
                      name="navigationLink"
                      label={t('eventEditor.form.navLink')}
                      required={false}
                  >
                      <Input />
                  </Form.Item>
                  <Form.Item
                      name="description"
                      label={t('eventEditor.form.description')}
                      required={false}
                  >
                      <TextArea maxLength={500} />
                  </Form.Item>
                  <Form.Item
                        name="mediaUrl"
                        label={t('eventEditor.form.mediaUrl')}
                        required={false}
                  >
                      <Input />
                  </Form.Item>
              </Form>
          </Modal>
          <List
            loading={isLoading}
            header={<AddButton onSave={createNewEvent} />}
            size={"small"}
            dataSource={events}
            renderItem={(event: EventDTO) =>
                <>
                    <Descriptions size={"small"} bordered title={event.title} layout={"vertical"} >
                        <DescriptionsItem label={<Typography.Text strong><Label trl="eventEditor.form.description" /></Typography.Text>}>
                            <Typography.Paragraph>{event.description}</Typography.Paragraph>
                        </DescriptionsItem>
                        <DescriptionsItem label={<Typography.Text strong>Published</Typography.Text>}>
                            {event.published ? <CheckOutlined style={{color: 'green'}}/> : <CloseOutlined style={{color: 'red'}}/>}
                        </DescriptionsItem>
                        <DescriptionsItem label={<Typography.Text strong><Label trl="eventEditor.form.location" /></Typography.Text>}>
                            <Typography.Text>{event.location}</Typography.Text>
                        </DescriptionsItem>
                        <DescriptionsItem label={<Typography.Text strong><Label trl="eventEditor.form.navLink" /></Typography.Text>}>
                            {!!event.navigationLink ? <a href={event.navigationLink}><Label trl="eventEditor.form.navLink" /></a> : <></>}
                        </DescriptionsItem>
                        <DescriptionsItem label={<Typography.Text strong><Label trl="eventEditor.form.startsAt" /></Typography.Text>}>
                            <Typography.Text>{moment(event.startsAt as any).format('YYYY-MM-DD HH:mm')}</Typography.Text>
                        </DescriptionsItem>
                        <DescriptionsItem label={<Typography.Text strong>Event Media</Typography.Text>}>
                            {!!event.mediaUrl ? <div style={{maxWidth: '100px'}}><Image src={event.mediaUrl} alt={'event media'} /></div> : <></>}
                        </DescriptionsItem>
                        <DescriptionsItem label={<Typography.Text strong>Reservation Link</Typography.Text>}>
                            {event && event.published &&
                                <Flex justify={"space-between"}>
                                    <Link to={`/reserve/${event.tag}`}>Reservation Link</Link>
                                    <Button icon={<CopyOutlined/>} onClick={() => {
                                        copyToClipboard(`${window.location.origin}/reserve/${event.tag}`);
                                    }}>Copy</Button>
                                </Flex>
                            }
                            {event && !event.published && <Typography.Text type={"warning"} >Event is not published yet</Typography.Text>}
                        </DescriptionsItem>
                        <DescriptionsItem label={<Typography.Text strong><Label trl="gameAdmin.table.actions" /></Typography.Text>}>
                            <>
                                <Button type={"link"} onClick={() => editEvent(event)}><Label trl="common.edit" /></Button>
                                <Link style={{color: 'green'}} to={`/admin/tables/${event.id}`}><Label trl="eventEditor.form.tables" /></Link>
                                <Button type={"link"} onClick={() => downloadAttendanceSheet(event)}>Attendance Sheet</Button>
                                <Popconfirm
                                    title={`Are you sure you want to cancel ${event.title} completely?`}
                                    onConfirm={() => {
                                        cancelEvent(event);
                                    }}>
                                    <Button type={"link"} style={{color: 'red'}} ><Label trl="common.cancel" /></Button>
                                </Popconfirm>
                                {!event.published &&
                                    <Popconfirm
                                        title={`Are you sure you want to publish ${event.title}? This cannot be undone.`}
                                        onConfirm={() => {
                                            publishEvent(event);
                                        }}>
                                        <Button type={"link"} style={{color: 'orange'}}>Publish</Button>
                                    </Popconfirm>
                                }
                            </>
                        </DescriptionsItem>
                    </Descriptions>
                    <Divider />
                </>
            }
          />
      </>
    );
}